/**
 * Farm Manager Settings Service
 * Handles loading and saving of FarmManager agent customization settings
 * 
 * Storage Path: Users/{userId}/Account/FarmManagerSettings
 */

import firebase from "firebase/compat/app";
import app from "../firebase";

const db = firebase.firestore(app);

/**
 * Default FarmManager settings
 */
export const DEFAULT_FARM_MANAGER_SETTINGS = {
        model: {
                name: "gemini-2.5-flash",
                temperature: 0.7,
                maxOutputTokens: 8192,
                thinkingBudget: -1, // -1 = dynamic (model decides), 0 = off, >0 = fixed token budget
        },
        knowledgeBase: {
                defaultSections: ["featureCapabilities", "operationalGuidance", "productionData"],
                enabledSections: {
                        livestockManagement: true,
                        healthIssues: true,
                        treatmentProtocols: true,
                        breedingCycle: true,
                        bionodeTroubleshooting: true,
                        featureCapabilities: true,
                        operationalGuidance: true,
                        productionData: true,
                },
        },
        conversation: {
                maxHistoryMessages: 12,
        },
        persona: {
                name: "Lisa",
                title: "digital farm manager",
        },
};

/**
 * Gets FarmManager settings for a user
 * @param {string} userId - The unique identifier for the user
 * @returns {Promise<Object>} FarmManager settings
 * @path Users/{userId}/Account/FarmManagerSettings
 */
export async function getFarmManagerSettings(userId) {
        try {
                const docRef = db
                        .collection("Users")
                        .doc(userId)
                        .collection("Account")
                        .doc("FarmManagerSettings");

                const documentSnapshot = await docRef.get();

                if (documentSnapshot.exists && documentSnapshot.data()) {
                        const data = documentSnapshot.data();
                        // Merge with defaults to ensure all fields exist
                        return {
                                model: {
                                        ...DEFAULT_FARM_MANAGER_SETTINGS.model,
                                        ...(data.model || {}),
                                },
                                knowledgeBase: {
                                        ...DEFAULT_FARM_MANAGER_SETTINGS.knowledgeBase,
                                        ...(data.knowledgeBase || {}),
                                        enabledSections: {
                                                ...DEFAULT_FARM_MANAGER_SETTINGS.knowledgeBase.enabledSections,
                                                ...(data.knowledgeBase?.enabledSections || {}),
                                        },
                                },
                                conversation: {
                                        ...DEFAULT_FARM_MANAGER_SETTINGS.conversation,
                                        ...(data.conversation || {}),
                                },
                                persona: {
                                        ...DEFAULT_FARM_MANAGER_SETTINGS.persona,
                                        ...(data.persona || {}),
                                },
                        };
                }

                // Return defaults if document doesn't exist
                return { ...DEFAULT_FARM_MANAGER_SETTINGS };
        } catch (error) {
                console.error("[farmManagerSettings][getFarmManagerSettings] - Error:", error);
                // Return defaults on error
                return { ...DEFAULT_FARM_MANAGER_SETTINGS };
        }
}

/**
 * Sets FarmManager settings for a user
 * @param {string} userId - The unique identifier for the user
 * @param {Object} settings - Settings to save (will be merged with existing)
 * @returns {Promise<void>}
 * @path Users/{userId}/Account/FarmManagerSettings
 */
export async function setFarmManagerSettings(userId, settings) {
        try {
                await db
                        .collection("Users")
                        .doc(userId)
                        .collection("Account")
                        .doc("FarmManagerSettings")
                        .set(settings, { merge: true });
        } catch (error) {
                console.error("[farmManagerSettings][setFarmManagerSettings] - Error:", error);
                throw error;
        }
}

